import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
} from 'react';

import type { LayoutMarker } from '../contracts/models';
import { LayoutMarkerGlyph } from './LayoutMarkerGlyph';

export interface LayoutMarkerPresentation {
  visible?: boolean;
  cursor?: CSSProperties['cursor'];
  opacity?: number;
  fill?: string;
  labelFill?: string;
  haloFill?: string;
  haloOpacity?: number;
  title?: string;
  className?: string;
}

export interface LayoutMarkerLayerProps {
  markers: readonly LayoutMarker[];
  editing?: boolean;
  dragThreshold?: number;
  isSelected?: (marker: LayoutMarker) => boolean;
  getPresentation?: (marker: LayoutMarker) => LayoutMarkerPresentation;
  onActivate?: (
    marker: LayoutMarker,
    event: ReactMouseEvent<SVGGElement>,
  ) => void;
  onHoverStart?: (
    marker: LayoutMarker,
    event: ReactMouseEvent<SVGGElement>,
  ) => void;
  onHoverMove?: (
    marker: LayoutMarker,
    event: ReactMouseEvent<SVGGElement>,
  ) => void;
  onHoverEnd?: (
    marker: LayoutMarker,
    event: ReactMouseEvent<SVGGElement>,
  ) => void;
  onDragStart?: (
    marker: LayoutMarker,
    event: ReactMouseEvent<SVGGElement>,
  ) => void;
  onContextMenu?: (
    marker: LayoutMarker,
    event: ReactMouseEvent<SVGGElement>,
  ) => void;
  onTouchStartCapture?: (
    marker: LayoutMarker,
    event: ReactTouchEvent<SVGGElement>,
  ) => void;
  onTouchEndCapture?: (
    marker: LayoutMarker,
    event: ReactTouchEvent<SVGGElement>,
  ) => void;
  onTouchCancelCapture?: (marker: LayoutMarker) => void;
  onDelete?: (
    marker: LayoutMarker,
    event: ReactMouseEvent<SVGCircleElement>,
  ) => void;
}

const DEFAULT_PRESENTATION: Required<
  Pick<LayoutMarkerPresentation, 'cursor' | 'opacity' | 'fill' | 'labelFill'>
> = {
  cursor: 'pointer',
  opacity: 1,
  fill: 'red',
  labelFill: 'white',
};

/**
 * Canonical marker collection layer shared by image and PDF layouts.
 *
 * Hosts resolve product-specific visibility and presentation, while this
 * component owns stable SVG structure and edit-mode drag intent detection.
 */
export const LayoutMarkerLayer = ({
  markers,
  editing = false,
  dragThreshold = 3,
  isSelected = () => false,
  getPresentation = () => DEFAULT_PRESENTATION,
  onActivate,
  onHoverStart,
  onHoverMove,
  onHoverEnd,
  onDragStart,
  onContextMenu,
  onTouchStartCapture,
  onTouchEndCapture,
  onTouchCancelCapture,
  onDelete,
}: LayoutMarkerLayerProps) => (
  <>
    {markers.map((marker) => {
      const presentation = {
        ...DEFAULT_PRESENTATION,
        ...getPresentation(marker),
      };
      if (presentation.visible === false) return null;

      const selected = isSelected(marker);
      const className = [
        'photo-marker',
        editing ? 'photo-index-active' : '',
        presentation.className ?? '',
      ]
        .filter(Boolean)
        .join(' ');

      return (
        <g
          key={marker.id}
          className={className}
          data-marker-number={marker.index}
          data-marker-index={marker.id}
          style={{
            cursor: presentation.cursor,
            pointerEvents: 'auto',
            opacity: presentation.opacity,
            transition: 'opacity 160ms ease',
          }}
          onClick={(event) => onActivate?.(marker, event)}
          onMouseEnter={(event) => onHoverStart?.(marker, event)}
          onMouseMove={(event) => onHoverMove?.(marker, event)}
          onMouseLeave={(event) => onHoverEnd?.(marker, event)}
          onTouchStartCapture={(event) =>
            onTouchStartCapture?.(marker, event)
          }
          onTouchEndCapture={(event) => onTouchEndCapture?.(marker, event)}
          onTouchCancelCapture={() => onTouchCancelCapture?.(marker)}
          onMouseDown={(event) => {
            const hasModifier =
              event.shiftKey || event.ctrlKey || event.metaKey;
            if (
              !editing ||
              hasModifier ||
              event.button !== 0 ||
              !onDragStart
            ) {
              return;
            }

            event.stopPropagation();
            const startX = event.clientX;
            const startY = event.clientY;
            let hasStarted = false;

            const handleMouseMove = (moveEvent: MouseEvent) => {
              if (
                !hasStarted &&
                (Math.abs(moveEvent.clientX - startX) > dragThreshold ||
                  Math.abs(moveEvent.clientY - startY) > dragThreshold)
              ) {
                hasStarted = true;
                onDragStart(marker, event);
              }
            };
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
          onContextMenu={(event) => {
            if (editing) onContextMenu?.(marker, event);
          }}
        >
          {presentation.title !== undefined && (
            <title>{presentation.title}</title>
          )}
          <LayoutMarkerGlyph
            x={marker.x}
            y={marker.y}
            width={marker.width}
            height={marker.height}
            fontSize={marker.fontSize}
            label={marker.index}
            selected={selected}
            fill={presentation.fill}
            labelFill={presentation.labelFill}
            haloFill={presentation.haloFill}
            haloOpacity={presentation.haloOpacity}
            showDeleteControl={editing && Boolean(onDelete)}
            onDelete={(event) => onDelete?.(marker, event)}
          />
        </g>
      );
    })}
  </>
);
